import React, { useLayoutEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useUserToken, useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { useEndLoginAsSession } from '../../../redux/slices/appInfo/useOnLogout';
import { loginAsAdminOf } from './loginAsClaim';
import styles from './LoginAsBanner.module.scss';

const messages = defineMessages({
  text: {
    id: 'app.sml.loginAs.banner.text',
    defaultMessage: 'Admin session: you are logged in as {email}. Opened by {adminEmail}.',
  },
  end: { id: 'app.sml.loginAs.banner.end', defaultMessage: 'End session' },
});

/** The shell reads this to stay below the banner (the sidebar, the mobile top bar, full-height pages). */
const HEIGHT_PROPERTY = '--m24-admin-banner-h';

/** Keeps the page and the fixed parts of the shell clear of the banner, whatever its height is. */
function useReserveBannerSpace(isShown: boolean): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const banner = ref.current;
    if (!isShown || banner === null) {
      return undefined;
    }
    const root = document.documentElement;
    const reserve = () => {
      const height = `${banner.offsetHeight}px`;
      root.style.setProperty(HEIGHT_PROPERTY, height);
      document.body.style.paddingTop = height;
    };
    reserve();
    // The text wraps on a narrow window, so the height changes with the width.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserve);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(HEIGHT_PROPERTY);
      document.body.style.removeProperty('padding-top');
    };
  }, [isShown]);

  return ref;
}

/**
 * The notice of an admin session ("Log in as this user"). It shows on every screen, above the
 * app, while the session token has the `loginAsAdmin` claim. A normal session renders nothing.
 */
const LoginAsBannerComponent: React.FC = () => {
  const intl = useIntl();
  const userToken = useUserToken();
  const decoded = useUserTokenDecoded();
  const adminEmail = loginAsAdminOf(decoded);
  const endSession = useEndLoginAsSession(userToken);
  const ref = useReserveBannerSpace(adminEmail !== null);

  if (adminEmail === null || decoded === null) {
    return null;
  }
  return (
    <div ref={ref} className={styles.banner} role="status" data-testid="login-as-banner">
      <span className={styles.text}>{intl.formatMessage(messages.text, { email: decoded.email, adminEmail })}</span>
      <button type="button" className={styles.end} onClick={endSession}>
        {intl.formatMessage(messages.end)}
      </button>
    </div>
  );
};

export const LoginAsBanner = TypedMemo(LoginAsBannerComponent);
